import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { TextSelect, X } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  label: {
    defaultMessage: 'Selected text',
    id: 'TqjJj/59t/',
    description:
      'Badge in the floating Ask Heidi bar showing that highlighted note text is attached as context',
  },
  remove: {
    defaultMessage: 'Remove selected text context',
    id: 'UPr9q7O0im',
    description:
      'Accessible name for the button that removes the highlighted-text context from the Ask Heidi bar',
  },
});

/**
 * Inline "Selected text ×" chip shown in the floating Ask Heidi bar while
 * highlighted note text is attached as context (selection → Ask Heidi).
 */
export const SelectedTextBadge = ({
  onRemove,
  className,
}: {
  onRemove: () => void;
  className?: string;
}) => {
  const intl = useIntl();

  return (
    <div
      data-testid="selected-text-badge"
      className={cn(
        // Same colour treatment as the in-note text selection highlight
        // (::selection uses bg-surface-selected-hover, sky family).
        'flex shrink-0 items-center gap-1 rounded-md bg-surface-selected py-0.5 pe-0.5 ps-1.5 text-sm text-text-link',
        className
      )}
    >
      <TextSelect size={14} className="shrink-0" />
      {intl.formatMessage(messages.label)}
      <Button
        type="button"
        variant="ghost"
        size="xxs"
        iconOnly
        onMouseDown={(event) => {
          // Keep focus off the note editor so the native selection does not
          // expand when dismissing the badge.
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
        aria-label={intl.formatMessage(messages.remove)}
        data-testid="selected-text-badge-remove"
        className="size-4 min-w-4 p-0 text-text-link hover:bg-surface-selected-hover hover:text-text-link-hover"
      >
        <X size={12} />
      </Button>
    </div>
  );
};
